import type { AppRegion } from '@/entities/region'
import { getStorageWithExpiry, removeStorage, setStorageWithExpiry } from './utils'

// Constants
const REGION_KEY = 'region'

/**
 * Region storage API for managing application region settings
 */
export const regionStorage = {
  /**
   * Gets the region from storage
   * @returns The region or null if not found
   */
  getRegion: (): AppRegion | null => {
    const regionValue = getStorageWithExpiry(REGION_KEY)
    return regionValue as AppRegion | null
  },

  /**
   * Sets the region in storage
   * @param regionValue - The region to store
   */
  setRegion: (regionValue: AppRegion): void => {
    setStorageWithExpiry(REGION_KEY, regionValue)
    sessionStorage.setItem(REGION_KEY, regionValue) // Use sessionStorage as a backup
  },

  /**
   * Clears the region from storage
   */
  clearRegion: (): void => {
    removeStorage(REGION_KEY)
    sessionStorage.removeItem(REGION_KEY)
  },

  /**
   * Syncs region between primary and backup storage
   */
  reSyncRegion: (): void => {
    const primaryValue = getStorageWithExpiry(REGION_KEY)
    const backupValue = sessionStorage.getItem(REGION_KEY)

    if (primaryValue !== null && backupValue === null) {
      // Restore backup
      sessionStorage.setItem(REGION_KEY, primaryValue)
    } else if (primaryValue === null && backupValue !== null) {
      // Restore primary
      setStorageWithExpiry(REGION_KEY, backupValue)
    } else if (primaryValue !== null && backupValue !== null && primaryValue !== backupValue) {
      // Values don't match, use primary as source of truth
      sessionStorage.setItem(REGION_KEY, primaryValue)
    }
  },
}
